import React, { useState } from 'react';
import { ComplianceRequirement, ComplianceStatus, RequirementRiskLevel } from './types';
import { TablePagination, useTablePagination } from '../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

interface Props {
  requirements: ComplianceRequirement[];
  onSelectRequirement: (req: ComplianceRequirement) => void;
  onCreateRequirement: (data: Partial<ComplianceRequirement>) => void;
  selectedRegulationId?: string;
}

export const ComplianceRequirementsList: React.FC<Props> = ({
  requirements,
  onSelectRequirement,
  onCreateRequirement,
  selectedRegulationId,
}) => {
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [filterRisk, setFilterRisk] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    regulation_id: selectedRegulationId || 'ORG-REG-01',
    req_code: '',
    title: '',
    requirement_statement: '',
    internal_interpretation: '',
    responsible_department: 'Flight Operations',
    responsible_owner: '',
    compliance_status: 'PENDING_ASSESSMENT' as ComplianceStatus,
    risk_level: 'MEDIUM' as RequirementRiskLevel,
  });

  const filtered = requirements.filter((r) => {
    if (filterStatus !== 'ALL' && r.compliance_status !== filterStatus) return false;
    if (filterRisk !== 'ALL' && r.risk_level !== filterRisk) return false;
    return true;
  });

  const { sortedItems, sortField, sortDirection, toggleSort } = useTableSort(filtered);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.req_code || !formData.title || !formData.requirement_statement) return;
    onCreateRequirement(formData);
    setShowModal(false);
    setFormData({
      regulation_id: selectedRegulationId || 'ORG-REG-01',
      req_code: '',
      title: '',
      requirement_statement: '',
      internal_interpretation: '',
      responsible_department: 'Flight Operations',
      responsible_owner: '',
      compliance_status: 'PENDING_ASSESSMENT',
      risk_level: 'MEDIUM',
    });
  };

  const getStatusBadge = (status: ComplianceStatus) => {
    switch (status) {
      case 'COMPLIANT':
        return <span className="badge-pill badge-success">Compliant</span>;
      case 'PARTIALLY_COMPLIANT':
        return <span className="badge-pill badge-warning">Partially Compliant</span>;
      case 'NON_COMPLIANT':
        return <span className="badge-pill badge-danger">Non-Compliant</span>;
      default:
        return <span className="badge-pill badge-neutral">Pending Assessment</span>;
    }
  };

  const getRiskBadge = (risk: RequirementRiskLevel) => {
    switch (risk) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      default:
        return <span className="badge-pill badge-neutral">Low</span>;
    }
  };

  return (
    <div className="card">
      {/* Table Toolbar */}
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 className="card-title">Compliance Requirements</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
            Operational interpretations and compliance evaluations against regulatory mandates
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff', color: '#334155' }}
          >
            <option value="ALL">All Compliance Statuses</option>
            <option value="COMPLIANT">Compliant</option>
            <option value="PARTIALLY_COMPLIANT">Partially Compliant</option>
            <option value="NON_COMPLIANT">Non-Compliant</option>
            <option value="PENDING_ASSESSMENT">Pending Assessment</option>
          </select>

          {/* Risk Filter */}
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff', color: '#334155' }}
          >
            <option value="ALL">All Risk Levels</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <button
            onClick={() => setShowModal(true)}
            className="btn-primary"
            style={{ fontSize: '13px', padding: '6px 14px' }}
          >
            + New Requirement
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="req_code" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Requirement Code & Title
              </SortableTh>
              <SortableTh field="regulation_title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Applicable Regulation
              </SortableTh>
              <SortableTh field="responsible_department" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort}>
                Department & Owner
              </SortableTh>
              <SortableTh field="risk_level" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ textAlign: 'center' }}>
                Risk
              </SortableTh>
              <SortableTh field="compliance_status" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ textAlign: 'center' }}>
                Status
              </SortableTh>
              <SortableTh field="is_covered" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ textAlign: 'center' }}>
                Coverage
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                  No requirements found matching criteria.
                </td>
              </tr>
            ) : (
              paginatedItems.map((req) => (
                <tr key={req.id}>
                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{req.title}</div>
                    <div style={{ fontSize: '12px', color: '#2563eb', fontFamily: 'monospace' }}>{req.req_code}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>
                      {req.requirement_statement}
                    </div>
                  </td>
                  <td style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>
                    {req.regulation_title || req.regulation_id}
                  </td>
                  <td>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>{req.responsible_department}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{req.responsible_owner}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>{getRiskBadge(req.risk_level)}</td>
                  <td style={{ textAlign: 'center' }}>{getStatusBadge(req.compliance_status)}</td>
                  <td style={{ textAlign: 'center' }}>
                    {req.is_covered ? (
                      <span className="badge-pill badge-info" style={{ fontWeight: 600 }}>
                        ✓ Procedure Mapped
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                        — Unmapped
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => onSelectRequirement(req)}
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      Details & Assessment →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="requirements"
      />

      {/* New Requirement Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="card" style={{ maxWidth: '540px', width: '100%', margin: '20px' }}>
            <div className="card-header">
              <h3 className="card-title">Define New Compliance Requirement</h3>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Requirement Code</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. CMP-ORO-GEN-200-A"
                    value={formData.req_code}
                    onChange={(e) => setFormData({ ...formData, req_code: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Risk Level</label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value as any })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                  >
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Requirement Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Periodic Emergency Response Plan Drill"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Regulatory Mandate Statement</label>
                <textarea
                  rows={2}
                  required
                  value={formData.requirement_statement}
                  onChange={(e) => setFormData({ ...formData, requirement_statement: e.target.value })}
                  placeholder="Mandatory regulatory requirement text..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Internal Operational Interpretation</label>
                <textarea
                  rows={2}
                  value={formData.internal_interpretation}
                  onChange={(e) => setFormData({ ...formData, internal_interpretation: e.target.value })}
                  placeholder="How this requirement is applied in airline operations..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Responsible Department</label>
                  <input
                    type="text"
                    required
                    value={formData.responsible_department}
                    onChange={(e) => setFormData({ ...formData, responsible_department: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Responsible Owner / Role</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. ERP Coordinator"
                    value={formData.responsible_owner}
                    onChange={(e) => setFormData({ ...formData, responsible_owner: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                  style={{ fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ fontSize: '13px' }}
                >
                  Save Requirement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
